import React, { useEffect, useMemo, useState } from "react";
import { Navigate } from "react-router-dom";
import PageBreadcrumb from "../../common/PageBreadcrumb/PageBreadcrumb";
import DocumentMasterReportTable from "../../common/documentMaster/DocumentMasterReportTable";
import {
  isDocumentMasterRowUsed,
  EISF_DOCUMENT_TYPE_IN_USE_MESSAGE,
} from "../../common/documentMaster/documentMasterUtils";
import { useLoader } from "../../../context/LoaderContext";
import useInfoAlert from "../../../hooks/useInfoAlert";
import InfoAlert from "../../common/InfoAlert";
import { useUserData } from "../../../hooks/useUserData";
import ConfirmModal from "../../common/ConfirmModal";
import {
  fetchEisfDocumentTypes,
  saveEisfDocumentType,
  deleteEisfDocumentType,
} from "../../../services/eisfMasterService";
import {
  parsePermissions,
  hasPermission,
  getAllPermissionKeys,
  isSystemAdministratorRole,
} from "../../../utils/permissionUtils";
import { organizationHasIsfModule } from "../../../utils/orgModuleAccess";
import {
  useDocumentMasterAbbreviationSuggest,
  EISF_DOCUMENT_ABBR_MAX_LENGTH,
} from "../../../hooks/useDocumentMasterAbbreviationSuggest";
import "../qms/QmsPages.scss";

const alphaNumRegex = /^[a-zA-Z0-9 ]+$/;

const EisfDocumentTypes = ({ embedded = false }) => {
  const { userData, loading: userLoading } = useUserData();
  const { showLoader, hideLoader } = useLoader();
  const { alertState, showAlert, hideAlert } = useInfoAlert();
  const isSystemAdmin = isSystemAdministratorRole(userData?.Role);

  const access = useMemo(() => {
    if (userLoading) return { status: "loading" };
    if (!organizationHasIsfModule(userData)) {
      return {
        status: "denied",
        message: "Your organisation does not have the eISF module.",
      };
    }
    const perms = isSystemAdmin
      ? getAllPermissionKeys()
      : parsePermissions(userData?.Permission || "");
    if (!isSystemAdmin && !hasPermission(perms, "EISF_CONFIGURE")) {
      return {
        status: "denied",
        message: "You need Can Configure eISF permission.",
      };
    }
    return { status: "allowed" };
  }, [userData, userLoading, isSystemAdmin]);

  const [rows, setRows] = useState([]);
  const [listLoading, setListLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingRow, setEditingRow] = useState(null);
  const [typeName, setTypeName] = useState("");
  const [abbreviation, setAbbreviation] = useState("");
  const [errors, setErrors] = useState({});
  const [confirmModal, setConfirmModal] = useState({ open: false, row: null });

  const layoutStorageKey = useMemo(
    () =>
      `eisf-doc-type-layout-v1:${String(userData?.OrganizationId || "org").toLowerCase()}`,
    [userData?.OrganizationId],
  );

  const { resetSuggestState, handleAbbreviationChange, abbrTouched } =
    useDocumentMasterAbbreviationSuggest({
      nameValue: typeName,
      showModal,
      setAbbreviation,
      maxLength: EISF_DOCUMENT_ABBR_MAX_LENGTH,
    });

  const openEditModal = (row) => {
    setEditingRow(row);
    setTypeName(row.DocumentType || "");
    setAbbreviation(row.Abberiviation || "");
    setErrors({});
    resetSuggestState(true);
    setShowModal(true);
  };

  const resetForm = () => {
    setEditingRow(null);
    setTypeName("");
    setAbbreviation("");
    setErrors({});
    resetSuggestState(false);
  };

  const loadDocumentTypes = async () => {
    if (!userData?.OrganizationId || !userData?.StaffId) return;
    try {
      setListLoading(true);
      const list = await fetchEisfDocumentTypes(userData);
      setRows(Array.isArray(list) ? list : []);
    } catch (error) {
      showAlert({
        title: "Error",
        message: error?.message || "Failed to load document types.",
        type: "danger",
      });
      setRows([]);
    } finally {
      setListLoading(false);
    }
  };

  useEffect(() => {
    if (access.status === "allowed") loadDocumentTypes();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [access.status, userData?.OrganizationId, userData?.StaffId]);

  const validate = () => {
    const next = {};
    if (!typeName.trim()) next.typeName = "Type Name is required.";
    else if (!alphaNumRegex.test(typeName.trim()))
      next.typeName = "Only letters, numbers and spaces are allowed.";
    if (!abbreviation.trim()) next.abbreviation = "Abbreviation is required.";
    else if (!alphaNumRegex.test(abbreviation.trim()))
      next.abbreviation = "Only letters, numbers and spaces are allowed.";
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const handleSave = async () => {
    if (!validate() || !userData?.OrganizationId || !userData?.StaffId) return;
    try {
      showLoader(editingRow ? "Updating type..." : "Saving type...");
      const { message } = await saveEisfDocumentType(userData, {
        ActionType: editingRow ? "UpdateDocumentType" : "SaveDocumentType",
        DocumentType: typeName.trim(),
        Abberiviation: abbreviation.trim(),
        DocumentTypeId: editingRow?.DocumentTypeId || "",
      });
      showAlert({ title: "Success", message, type: "success" });
      setShowModal(false);
      resetForm();
      await loadDocumentTypes();
    } catch (error) {
      showAlert({
        title: "Error",
        message: error?.message || "Failed to save document type.",
        type: "danger",
      });
    } finally {
      hideLoader();
    }
  };

  const handleDeleteClick = (row, event) => {
    event?.stopPropagation?.();
    if (isDocumentMasterRowUsed(row)) {
      showAlert({
        title: "Cannot delete",
        message: "This document type is used in eISF and cannot be deleted.",
        type: "warning",
      });
      return;
    }
    setConfirmModal({ open: true, row });
  };

  const handleDeleteConfirm = async () => {
    const row = confirmModal.row;
    setConfirmModal({ open: false, row: null });
    if (!row) return;
    try {
      showLoader("Deleting document type...");
      const { message } = await deleteEisfDocumentType(
        userData,
        row.DocumentTypeId,
      );
      showAlert({ title: "Deleted", message, type: "success" });
      await loadDocumentTypes();
    } catch (error) {
      showAlert({
        title: "Error",
        message: error?.message || "Failed to delete document type.",
        type: "danger",
      });
    } finally {
      hideLoader();
    }
  };

  if (access.status === "loading") return null;
  if (access.status === "denied") {
    if (embedded) {
      return (
        <div className="alert alert-warning mb-0" role="alert">
          <strong>Access restricted.</strong> {access.message}
        </div>
      );
    }
    return <Navigate to="/notification-center" replace />;
  }

  const toolbarActions = [
    { icon: "fas fa-refresh", label: "Refresh", onClick: loadDocumentTypes },
    {
      icon: "fas fa-plus",
      label: "Add New Type",
      onClick: () => {
        resetForm();
        setShowModal(true);
      },
    },
  ];

  const isViewOnly = Boolean(editingRow && isDocumentMasterRowUsed(editingRow));

  return (
    <div className={`${embedded ? "" : "user-mgmt-page "}qms-document-types${embedded ? " qms-document-types--embedded" : ""}`}>
      <InfoAlert isOpen={alertState.isOpen} onClose={hideAlert} {...alertState} />
      {embedded ? (
        <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 mb-3">
          <div>
            <h6 className="mb-1 fw-semibold">Document Type</h6>
            <p className="text-muted small mb-0">
              Manage document types used in eISF study binders.
            </p>
          </div>
          <div className="d-flex flex-wrap gap-2">
            {toolbarActions.map((action) => (
              <button
                key={action.label}
                type="button"
                className="btn btn-outline-secondary btn-sm"
                onClick={action.onClick}
              >
                <i className={`${action.icon} me-1`} aria-hidden="true" />
                {action.label}
              </button>
            ))}
          </div>
        </div>
      ) : (
        <PageBreadcrumb
          items={[
            { label: "Home", path: "/notification-center" },
            { label: "eISF Config", path: "/superadmin/eisf-config" },
            { label: "Document Type" },
          ]}
          actions={toolbarActions}
        />
      )}
      <DocumentMasterReportTable
        rows={rows}
        nameField="DocumentType"
        nameColumnLabel="Type Name"
        layoutStorageKey={layoutStorageKey}
        onEdit={openEditModal}
        onDelete={handleDeleteClick}
        loading={listLoading}
      />
      <ConfirmModal
        isOpen={confirmModal.open}
        onClose={() => setConfirmModal({ open: false, row: null })}
        onConfirm={handleDeleteConfirm}
        title="Delete document type"
        message={
          confirmModal.row
            ? `Do you want to delete document type "${confirmModal.row.DocumentType}"?`
            : ""
        }
        confirmText="Yes"
        cancelText="No"
        confirmButtonClass="btn-danger"
      />
      {showModal && (
        <div className="modal fade show d-block" style={{ backgroundColor: "rgba(0,0,0,0.5)" }}>
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">
                  {isViewOnly
                    ? "View Document Type"
                    : editingRow
                      ? "Edit Document Type"
                      : "Add Document Type"}
                </h5>
                <button type="button" className="btn-close" onClick={() => setShowModal(false)} />
              </div>
              <div className="modal-body">
                {isViewOnly ? (
                  <div className="alert alert-warning py-2 mb-3" role="alert">
                    {EISF_DOCUMENT_TYPE_IN_USE_MESSAGE}
                  </div>
                ) : null}
                <div className="mb-3">
                  <label>Type Name <span className="mandatory">*</span></label>
                  <input
                    className={`form-control ${errors.typeName ? "is-invalid" : ""}`}
                    value={typeName}
                    onChange={(e) => setTypeName(e.target.value)}
                    readOnly={isViewOnly}
                    disabled={isViewOnly}
                  />
                  {errors.typeName && <div className="invalid-feedback d-block">{errors.typeName}</div>}
                </div>
                <div className="mb-3">
                  <label>Abbreviation <span className="mandatory">*</span></label>
                  <input
                    className={`form-control ${errors.abbreviation ? "is-invalid" : ""}`}
                    value={abbreviation}
                    onChange={(e) => handleAbbreviationChange(e.target.value)}
                    maxLength={EISF_DOCUMENT_ABBR_MAX_LENGTH}
                    readOnly={isViewOnly}
                    disabled={isViewOnly}
                  />
                  {!isViewOnly && !abbrTouched && typeName.trim() ? (
                    <div className="form-text">Suggested from type name</div>
                  ) : null}
                  {errors.abbreviation && (
                    <div className="invalid-feedback d-block">{errors.abbreviation}</div>
                  )}
                </div>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={() => setShowModal(false)}>
                  {isViewOnly ? "Close" : "Cancel"}
                </button>
                {!isViewOnly ? (
                  <button type="button" className="btn btn-primary" onClick={handleSave}>
                    {editingRow ? "Update" : "Save"}
                  </button>
                ) : null}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default EisfDocumentTypes;
